"use client";

import { getApiClient } from "@/shared/api/axios";
import { handleApiError } from "@/shared/api/error-handler";
import {
  UserCreateType,
  UserResponseType,
  UsersResponseType,
  UserUpdateType,
  AccessHistoryResponseType
} from "./user.type";

/**
 * Cria um novo usuário
 * @param data Dados do usuário
 * @returns Resposta da API com o usuário criado
 */
export async function createUserApi(data: UserCreateType): Promise<UserResponseType> {
  try {
    const api = await getApiClient();
    const response = await api.post<UserResponseType>("/user", data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Busca todos os usuários
 * @returns Resposta da API com a lista de usuários
 */
export async function findAllUsersApi(): Promise<UsersResponseType> {
  try {
    const api = await getApiClient();
    const response = await api.get<UsersResponseType>("/users");
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Busca um usuário pelo UUID
 * @param uuid UUID do usuário
 * @returns Resposta da API com o usuário
 */
export async function findOneUserApi(uuid: string): Promise<UserResponseType> {
  try {
    const api = await getApiClient();
    const response = await api.get<UserResponseType>(`/user/${uuid}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Atualiza um usuário
 * @param uuid UUID do usuário
 * @param data Dados do usuário
 * @returns Resposta da API com o usuário atualizado
 */
export async function updateUserApi(uuid: string, data: UserUpdateType): Promise<UserResponseType> {
  try {
    const api = await getApiClient();
    const response = await api.patch<UserResponseType>(`/user/${uuid}`, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Exclui um usuário
 * @param uuid UUID do usuário
 * @returns Resposta da API
 */
export async function deleteUserApi(uuid: string): Promise<UserResponseType> {
  try {
    const api = await getApiClient();
    const response = await api.delete<UserResponseType>(`/user/${uuid}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Busca os dados do usuário autenticado
 * @returns Resposta da API com os dados do usuário autenticado
 */
export async function getCurrentUserApi(): Promise<UserResponseType> {
  try {
    const api = await getApiClient();
    const response = await api.get<UserResponseType>("/user/me");
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Busca todos os usuários para administração
 * @returns Resposta da API com a lista de usuários para administração
 */
export async function findAllAdminUsersApi(): Promise<UsersResponseType> {
  try {
    const api = await getApiClient();
    const response = await api.get<UsersResponseType>("/admin/users");
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Busca o histórico de acesso do usuário autenticado
 * @returns Resposta da API com o histórico de acesso
 */
export async function getAccessHistoryApi(): Promise<AccessHistoryResponseType> {
  try {
    const api = await getApiClient();
    const response = await api.get<AccessHistoryResponseType>("/user/access-history");
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

